import { TFunction } from 'react-i18next'
import { Dispatch } from 'redux'

import { HelpDeskEnURL, HelpDeskFrURL } from 'lib/GlobalConstants'
import { ShowAlert } from 'types/Alert'
import {
  resetFilters,
  setSegmentId,
  toggleHasFilterValueStatus,
} from 'redux/slices/FilterBuilder'
import { setActiveGroup } from 'redux/slices/ParticipantGroups'
import { InputRefType } from 'types/Genric'
import { resetQuestions as resetSignUpFormQuestions } from 'redux/slices/SignUpQuestions'

export const getImportUsersHelpDeskURL = (locale: string) =>
  locale === 'en'
    ? `${HelpDeskEnURL}/articles/3575335-bulk-importing-participants-into-engagementhq`
    : `${HelpDeskFrURL}/articles/3575335-l-importation-de-participants-en-masse-dans-engagementhq`

export const INVALID_CSV = 'invalid_csv'
export const INVALID_FILE_SIZE = 'invalid_file_size'
export const PARTIAL_IMPORT = 'partial_import'
export const BLANK_CSV = 'blank_csv'

export const createAlertMetaData = (
  errorCode: string,
  t: TFunction,
  total?: number,
  processed?: number,
) => {
  switch (errorCode) {
    case BLANK_CSV:
      return {
        title: t('participant.importUsers.errors.blankCSV.title'),
        subTitle: '',
      }
    case PARTIAL_IMPORT:
      return {
        title: t('participant.importUsers.errors.partialImport.title'),
        subTitle: t('participant.importUsers.errors.partialImport.subTitle', {
          processedUsers: processed,
          totalUsers: total,
        }),
      }
    case INVALID_CSV:
      return {
        title: t('participant.importUsers.errors.invalidCSV.title'),
        subTitle: t('participant.importUsers.errors.invalidCSV.subTitle'),
      }
    case INVALID_FILE_SIZE:
      return {
        title: t('participant.importUsers.errors.invalidFileSize.title'),
        subTitle: t('participant.importUsers.errors.invalidFileSize.subTitle'),
      }
    default:
      return {
        title: '',
        subTitle: '',
      }
  }
}
/* 
  HandleFileUpload creates a metadata object for Alertbox
  and it calls showSuccessSnackBar function to show success snackbar.
*/
export const handleFileUpload = async (
  e: React.ChangeEvent<HTMLInputElement>,
  importUsersAPI: any,
  showAlert: ShowAlert,
  handleCancel: () => void,
  getBaseURL: () => string,
  t: TFunction,
  dispatch: Dispatch,
  onClearFilter: () => void,
  searchInputRef: InputRefType,
) => {
  const response = {
    title: '',
    subTitle: '',
    error: '',
    templateURL: createDownloadCSVTemplateURL('', getBaseURL),
  }
  // We have to check incase of empty event object
  if (e?.target?.files) {
    const csvFile = e.target.files[0]
    // Checking on Frontend for size of csv file
    if (isFileSizeMoreThan1MB(csvFile)) {
      const { title, subTitle } = createAlertMetaData(INVALID_FILE_SIZE, t)
      return {
        title: title,
        subTitle: subTitle,
        error: INVALID_FILE_SIZE,
        templateURL: createDownloadCSVTemplateURL(
          INVALID_FILE_SIZE,
          getBaseURL,
        ),
      }
    } else {
      // Else uploading the file
      const apiResponse = await importUsersAPI.importUsers(csvFile)
      const { data: { data: attributes } = { data: {} }, code = '' } =
        apiResponse

      //  If blank csv file was imported
      const errorCode =
        code === 'ERR_BAD_REQUEST' && apiResponse?.request?.response
          ? getErrorCodeFromAPIResponse(apiResponse)
          : ''
      if (code === 'ERR_BAD_REQUEST' && errorCode == 'blank') {
        const { title, subTitle } = createAlertMetaData(BLANK_CSV, t, 0, 0)
        return {
          title: title,
          subTitle: subTitle,
          error: BLANK_CSV,
          templateURL: createDownloadCSVTemplateURL(BLANK_CSV, getBaseURL),
        }
      }

      // If user imported invalid file type
      if (code === 'ERR_BAD_REQUEST' || attributes.length === 0) {
        const { title, subTitle } = createAlertMetaData(INVALID_CSV, t)
        return {
          title: title,
          subTitle: subTitle,
          error: INVALID_CSV,
          templateURL: createDownloadCSVTemplateURL(INVALID_CSV, getBaseURL),
        }
      }

      // id of the csv sheet
      // imported : true is more than 0 participants imported else false
      // invalid: true is more than 0 participants invalid else false
      // total: total participants which admin imported
      // total: processed participants which admin imported
      const { imported, invalid, total, processed, id } = attributes

      // If all participants are imported
      if (imported && !invalid) {
        showSuccessSnackBar(
          showAlert,
          handleCancel,
          processed,
          t,
          dispatch,
          onClearFilter,
          searchInputRef,
        )
        return response
      }

      // If few / no participants imported
      if (invalid) {
        const { title, subTitle } = createAlertMetaData(
          PARTIAL_IMPORT,
          t,
          total,
          processed,
        )
        return {
          title: title,
          subTitle: subTitle,
          error: PARTIAL_IMPORT,
          templateURL: createDownloadCSVTemplateURL(
            PARTIAL_IMPORT,
            getBaseURL,
            id,
          ),
        }
      }

      return response
    }
  }
  return response
}

// Function to know if file size in greater thant 1MB
export const isFileSizeMoreThan1MB = (csvFile: File) =>
  csvFile.size / 1024 / 1024 > 1

// Function to create URL for download csv template button
export const createDownloadCSVTemplateURL = (
  error: string,
  getBaseURL: () => string,
  id?: number,
) => {
  if (
    error === INVALID_FILE_SIZE ||
    error === INVALID_CSV ||
    error === BLANK_CSV ||
    !error
  ) {
    return `${getBaseURL()}/api/v2/participant_imports/template`
  } else {
    return createInvalidFileURL(id, getBaseURL)
  }
}

// Function to create URL for download error csv template in case of error = PARTIAL_IMPORT | INVALID_CSV
export const createInvalidFileURL = (
  id: number | undefined,
  getBaseURL: () => string,
) => `${getBaseURL()}/api/v2/participant_imports/${id}/download_invalid_file`

type Options = 'invite' | 'import'

// Function to handle swatich case of actions in PRM grid page
export const handleDialogueSelection = (
  option: Options,
  setDialogue: (arg0: string) => void,
) => {
  switch (option) {
    case 'invite':
      setDialogue('invite')
      break

    case 'import':
      setDialogue('import')
      break
  }
}

// Function to show success snackbar in case of all participants are imported and close Dialogue
const showSuccessSnackBar = (
  showAlert: ShowAlert,
  handleCancel: () => void,
  processed: number,
  t: TFunction,
  dispatch: Dispatch,
  onClearFilter: () => void,
  searchInputRef: InputRefType,
) => {
  showAlert({
    type: 'success',
    title: t('participant.importUsers.successAlert', {
      processedUsers: processed,
    }),
  })
  // clear the filters, group , search text and resets the grid
  dispatch(toggleHasFilterValueStatus(false))
  dispatch(resetSignUpFormQuestions())
  dispatch(resetFilters())
  dispatch(setActiveGroup(null))
  dispatch(setSegmentId(0))

  searchInputRef.current.value = ''
  if (searchInputRef.current.blur) searchInputRef.current.blur()
  onClearFilter()
  handleCancel()
}

const getErrorCodeFromAPIResponse = (response: any) => {
  return JSON.parse(response?.request?.response)?.errors[0].code || ''
}
